import { useState } from 'react'
import rangeParser from 'parse-numeric-range'
import Highlight, { defaultProps, Language } from 'prism-react-renderer'
import Editor from 'react-simple-code-editor'
import theme from 'prism-react-renderer/themes/nightOwl'
import clsx from 'clsx'
import { CopyButton } from '../CopyButton'

// Create a closure that determines if we have
// to highlight the given index
const calculateLinesToHighlight = (
  meta: string
): [(index: number) => boolean, boolean] => {
  const RE = /{([\d,-]+)}/
  if (RE.test(meta)) {
    const match = RE.exec(meta)
    if (match) {
      const strlineNumbers = match[1]
      const lineNumbers = rangeParser(strlineNumbers)
      return [(index: number) => lineNumbers.includes(index + 1), true]
    }
  }
  return [() => false, false]
}

export function Fence({
  children,
  onChange,
  language,
  queryType = '',
  highlights = '',
  shouldWrap = false,
  includeCopyButton = true,
  className = {
    'bg-color': 'bg-[#1E293B]',
  },
  topRightElements = (CopyButton) => <>{CopyButton}</>,
}: {
  children: string
  onChange?: (code: string) => void
  language?: Language
  queryType?: string
  highlights?: string
  shouldWrap?: boolean
  includeCopyButton?: boolean
  className?: {
    'bg-color'?: string
    container?: string
    code?: string
  }
  topRightElements?: (CopyButton: React.ReactNode) => React.ReactNode
}) {
  const [shouldHighlightLine, hasHighlights] =
    calculateLinesToHighlight(highlights)

  const [hovered, setHovered] = useState(false)

  const codeText = children.trimEnd()

  if (queryType == 'INSERT' && codeText.length > 20000) {
    language = undefined
  }

  const code = children.trimEnd()

  function highlight(code: string) {
    return (
      <Highlight
        {...defaultProps}
        code={code.trimEnd()}
        // @ts-ignore
        language={language}
        theme={theme}
      >
        {({ className, tokens, getLineProps, getTokenProps }) => (
          <pre className={clsx(`${className}`)}>
            <code
              className={clsx(
                shouldWrap ? 'whitespace-pre-wrap break-words' : ''
              )}
            >
              {tokens.map((line, lineIndex) => {
                const lineProps = getLineProps({ line, key: lineIndex })
                if (!shouldHighlightLine(lineIndex) && hasHighlights) {
                  lineProps.className = `${lineProps.className} opacity-70`
                } else if (shouldHighlightLine(lineIndex)) {
                  lineProps.className = `${lineProps.className} -mx-4 pl-3 border-l-4 border-sky-400 bg-sky-300/[0.15]`
                }
                return (
                  <div key={lineIndex} {...lineProps}>
                    {line
                      .filter((token) => !token.empty)
                      .map((token, tokenIndex) => (
                        <span key={tokenIndex} {...getTokenProps({ token })} />
                      ))}
                    {'\n'}
                  </div>
                )
              })}
            </code>
          </pre>
        )}
      </Highlight>
    )
  }

  return (
    <div
      className={clsx(
        'relative overflow-auto rounded-md',
        className.container,
        className['bg-color']
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="sticky top-0 z-10">
        <div className={`absolute right-5 top-3`}>
          {includeCopyButton &&
            topRightElements(
              <CopyButton hovered={hovered} content={codeText} />
            )}
        </div>
      </div>
      <div className="h-full">
        <div className={clsx(className.code, 'p-3 pr-5')}>
          {onChange ? (
            <Editor
              value={code}
              onValueChange={onChange}
              highlight={highlight}
              className={clsx('font-mono caret-white', className.code)}
            />
          ) : (
            highlight(code)
          )}
        </div>
      </div>
    </div>
  )
}
